/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateCreator } from 'zustand';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { createAppStore } from '@marriott/mi-store-utils';
import { PLATINUM_CORE_CODE } from '../utils';
import { GiftCardDetails } from './profileStore';

export interface memberDataState {
  customer: {
    profile: {
      birthday?: string;
      name: {
        givenName: string;
        fullName?: string;
      };
      localizedName?: {
        givenName: string;
        surname: string;
        fullName?: string;
      };
    };
    loyaltyInformation: {
      rewards: {
        achieveLevelReasonCode?: string;
        datePointsExpire: string;
        enrollmentDate: string;
        currentPointBalance: number;
        eliteLifetimeNights?: number;
        number: number;
        eliteNightsToAchieveNext: number;
        eliteNightsToRenewCurrent: number;
        eliteLifetimeLevel: {
          code: string;
        };
        levelChangeDate?: string;
        level: {
          code: string;
        };
        levelType: {
          code: string;
        };
        nextLevelType: {
          code: string;
        };
        nextLevel: {
          code: string;
        };
        totalRevenue?: number;
      };
      rewardsSummary: {
        yearly: {
          totalNights: number;
          stayNights?: number;
          year: number;
          totalRevenue: number;
        }[];
      };
    };
    contactInformation?: {
      emails: {
        address: string;
        primary: boolean;
      }[];
      phones?: {
        country?: {
          code?: string;
        };
        number?: string;
      };
    };
  };
}

export interface memberDataUxlState {
  currentPoints: number | undefined;
  numberOfNights: number | undefined;
  memberLevelCode: string;
  memberLevelTypeCode: string;
  givenName: string;
  localizedName?: string;
  localizedLastName?: string;
}

type profileNudgePayload = {
  transactionId?: string;
  communicationToken?: string;
  isNudgeVerify?: boolean;
  fourDigitOfMobile?: string;
};

export interface PersistentGiftCardDetails extends GiftCardDetails {
  cardNumber?: string;
  securityCode?: string;
}

export interface AccountPersistentState {
  memberLevel: string;
  memberData: memberDataState | null;
  memberLevelCode: string;
  memberLevelTypeCode: string;
  memberName: string;
  memberLocalizedName?: string;
  fullName?: string;
  localizedFullName?: string;
  memberLocalizedLastName?: string;
  currentPointBalance: number | undefined;
  totalNightCount: number | undefined;
  setMemberDetails?: (value: memberDataState) => void;
  setMemberDetailsUxl?: (value: memberDataUxlState) => void;
  communicationOption?: string;
  setCommunicationOption?: (value: string) => void;
  setCurrentPointsBalance?: (value: number) => void;
  setMemberData?: (value: memberDataState | null) => void;
  loginEventDetails?: string;
  setLoginEventDetails?: (value: string) => void;
  setPromoInvalid?: (value: boolean) => void;
  promoInvalid?: boolean;
  setPromoRegistered?: (value: string | undefined) => void;
  promoRegistered?: string;
  isEAAEnrollFailed?: boolean;
  setIsEAAEnrollFailed?: (value?: boolean) => void;
  showBrowserRegistrationCta?: boolean;
  setShowBrowserRegistrationCta?: (value?: boolean) => void;
  profileNudgePayload?: profileNudgePayload;
  setProfileNudgePayload: (val: profileNudgePayload) => void;
  isDTTFlag?: boolean;
  setIsDTTFlag: (value: boolean) => void;
  passwordChanged?: boolean;
  setPasswordChanged: (value: boolean) => void;
  userAwards?: number;
  setUserAwards: (value: number) => void;
  isHRTFlow?: boolean;
  setIsHRTFlow: (value: boolean) => void;
  maskedPhone?: string;
  setMaskedPhone: (value: string) => void;
}

const initialState: AccountPersistentState = {
  memberLevel: '',
  memberData: null,
  memberLevelCode: '',
  memberLevelTypeCode: '',
  fullName: '',
  localizedFullName: '',
  memberName: '',
  memberLocalizedName: '',
  memberLocalizedLastName: '',
  currentPointBalance: undefined,
  totalNightCount: undefined,
  loginEventDetails: '',
  setLoginEventDetails: () => {},
  setMemberDetails: () => {},
  setMemberDetailsUxl: () => {},
  communicationOption: '',
  setCommunicationOption: () => {},
  setCurrentPointsBalance: () => {},
  setPromoInvalid: () => {},
  promoInvalid: false,
  setPromoRegistered: () => {},
  promoRegistered: '',
  isEAAEnrollFailed: false,
  setIsEAAEnrollFailed: () => {},
  showBrowserRegistrationCta: false,
  setShowBrowserRegistrationCta: () => {},
  setProfileNudgePayload: () => {},
  profileNudgePayload: {
    isNudgeVerify: false,
    communicationToken: '',
    transactionId: '',
    fourDigitOfMobile: '',
  },
  isDTTFlag: false,
  setIsDTTFlag: () => {},
  passwordChanged: false,
  setPasswordChanged: () => {},
  userAwards: undefined,
  setUserAwards: () => {},
  isHRTFlow: false,
  setIsHRTFlow: () => {},
  maskedPhone: '',
  setMaskedPhone: () => {},
};

const AccountPersistentStore: StateCreator<AccountPersistentState> = set => {
  return {
    ...initialState,
    setMemberDetails: (value: memberDataState) => {
      const memberRibbon = value?.customer;
      const memberCode = memberRibbon?.loyaltyInformation?.rewards?.level?.code;
      const memberLevelType = memberRibbon?.loyaltyInformation?.rewards?.levelType?.code;
      const name = memberRibbon?.profile?.name?.givenName;
      const localizedName = memberRibbon?.profile?.localizedName?.givenName;
      const localizedLastName = memberRibbon?.profile?.localizedName?.surname;
      const pointBalance = memberRibbon?.loyaltyInformation?.rewards?.currentPointBalance;
      const nightCount = memberRibbon?.loyaltyInformation?.rewardsSummary?.yearly?.[0]?.totalNights;
      const fullName = memberRibbon?.profile?.name?.fullName;
      const localizedFullName = memberRibbon?.profile?.localizedName?.fullName;

      set({
        memberData: value,
        memberLevelCode: memberCode,
        memberLevelTypeCode: memberLevelType,
        memberName: name,
        memberLocalizedName: localizedName,
        memberLocalizedLastName: localizedLastName,
        currentPointBalance: pointBalance,
        totalNightCount: nightCount,
        fullName: fullName,
        localizedFullName: localizedFullName,
        memberLevel: memberCode?.toUpperCase() === PLATINUM_CORE_CODE && memberLevelType ? memberLevelType : memberCode,
      });
    },

    setMemberDetailsUxl: (data: memberDataUxlState) => {
      set({
        memberLevelCode: data?.memberLevelCode,
        memberLevelTypeCode: data?.memberLevelTypeCode,
        memberName: data?.givenName,
        memberLocalizedName: data?.localizedName,
        currentPointBalance: Number(data?.currentPoints ?? ''),
        totalNightCount: Number(data?.numberOfNights ?? ''),
        memberLevel:
          data?.memberLevelCode?.toUpperCase() === PLATINUM_CORE_CODE && data?.memberLevelTypeCode
            ? data?.memberLevelTypeCode
            : data?.memberLevelCode,
      });
    },

    setCommunicationOption: (value: string) => {
      set({ communicationOption: value });
    },

    setCurrentPointsBalance: (value: number) => {
      set({ currentPointBalance: value });
    },

    setLoginEventDetails: (value: string) => {
      set({ loginEventDetails: value });
    },

    setMemberData: (value: memberDataState | null) => {
      set({ memberData: value });
    },
    setPromoInvalid: (value?: boolean) => {
      set({ promoInvalid: value });
    },
    setPromoRegistered: (value: string | undefined) => {
      set({ promoRegistered: value });
    },
    setIsEAAEnrollFailed: (value?: boolean) => {
      set({ isEAAEnrollFailed: value });
    },
    setShowBrowserRegistrationCta: (value?: boolean) => {
      set({ showBrowserRegistrationCta: value });
    },
    setProfileNudgePayload: (val: profileNudgePayload) => {
      set(prevState => ({
        ...prevState,
        profileNudgePayload: {
          ...prevState.profileNudgePayload,
          ...val,
        },
      }));
    },
    setIsDTTFlag: (value?: boolean) => {
      set({ isDTTFlag: value });
    },
    setPasswordChanged: (value?: boolean) => {
      set({ passwordChanged: value });
    },

    setUserAwards: (value?: number) => {
      set({ userAwards: value });
    },

    setIsHRTFlow: (value?: boolean) => {
      set({ isHRTFlow: value });
    },

    setMaskedPhone: (value: string) => {
      set({ maskedPhone: value });
    },
  };
};

export const useAccountPersistentStore = createAppStore(AccountPersistentStore, {
  usePersistentStore: true,
  persistentStoreName: 'AccountPersistentState',
});
